import { Schema } from "effect";
import {
  AccountId,
  ConversationId,
  ConversationThreadId,
  ShopperAiIntegrationId,
} from "../../../ids/definitions";
import { DateFromDb } from "../../../schema/helpers";
import { MessageChannel } from "./message";
import { ConversationProduct } from "./product";
import {
  ChatFlowThreadChannelData,
  EmailThreadChannelData,
  MetaCommentThreadChannelData,
  SmsThreadChannelData,
  ThreadAnalyticsData,
} from "./thread/channel-data";

const threadBaseFields = {
  id: ConversationThreadId,
  conversationId: ConversationId,
  integrationId: ShopperAiIntegrationId,
  accountId: AccountId,
  externalThreadId: Schema.String,
  channel: MessageChannel,
  products: Schema.Array(ConversationProduct),
  // DateFromDb accepts both an ISO string (fresh off the wire) and a Date
  // instance (already-decoded), so the collection's schema re-validation on
  // writeUpsert doesn't reject the client-side value.
  createdAt: DateFromDb,
  analyticsData: Schema.NullOr(ThreadAnalyticsData),
};

const EmailConversationThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.Union(
    Schema.Literal("gmail"),
    Schema.Literal("outlook"),
  ),
  channelData: Schema.NullOr(EmailThreadChannelData),
});

const MetaCommentConversationThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.Union(
    Schema.Literal("facebook"),
    Schema.Literal("instagram"),
  ),
  channelData: Schema.NullOr(MetaCommentThreadChannelData),
});

const ChatFlowConversationThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.Literal("redoChatFlow"),
  channelData: Schema.NullOr(ChatFlowThreadChannelData),
});

const SmsConversationThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.Literal("telnyx_sms"),
  channelData: Schema.NullOr(SmsThreadChannelData),
});

const NullChannelConversationThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.Union(
    Schema.Literal("tiktok"),
    Schema.Literal("attentive"),
    Schema.Literal("postscript"),
    Schema.Literal("redoChatAi"),
    Schema.Literal("redoChatLive"),
    Schema.Literal("contactForm"),
  ),
  channelData: Schema.Null,
});

export const ConversationThread = Schema.Union(
  EmailConversationThread,
  MetaCommentConversationThread,
  ChatFlowConversationThread,
  SmsConversationThread,
  NullChannelConversationThread,
);
export type ConversationThread = typeof ConversationThread.Type;

export const ConversationThreadCursor = Schema.Struct({
  id: ConversationThreadId,
  createdAt: DateFromDb,
});
export type ConversationThreadCursor = typeof ConversationThreadCursor.Type;
